import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@/shared/constants/routes';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { AuthGuard } from '@/shared/components/guards/AuthGuard';
import { SuperAdminGuard } from '@/shared/components/guards/SuperAdminGuard';
import { GuestGuard } from '@/shared/components/guards/GuestGuard';
import { CustomerOnlyGuard } from '@/shared/components/guards/CustomerOnlyGuard';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { Skeleton } from '@/shared/components';
import { useVisitTracker } from '@/shared/hooks/useVisitTracker';

// Lazy-loaded pages
const AuthPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.AuthPage })),
);
const OnboardingPage = lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.OnboardingPage })),
);
const OverviewPage = lazy(() =>
  import('@/features/overview').then((m) => ({ default: m.OverviewPage })),
);
const BrandPage = lazy(() =>
  import('@/features/brand').then((m) => ({ default: m.BrandPage })),
);
const BrandSetupPage = lazy(() =>
  import('@/features/brand').then((m) => ({ default: m.BrandSetupPage })),
);
const PlanPage = lazy(() =>
  import('@/features/plan').then((m) => ({ default: m.PlanPage })),
);
const ComposerPage = lazy(() =>
  import('@/features/composer').then((m) => ({ default: m.ComposerPage })),
);
const NotificationsPage = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.NotificationsPage })),
);
const ApprovalsPage = lazy(() =>
  import('@/features/composer').then((m) => ({ default: m.ApprovalsPage })),
);
const InboxPage = lazy(() =>
  import('@/features/inbox').then((m) => ({ default: m.InboxPage })),
);
const InboxSettingsPage = lazy(() =>
  import('@/features/inbox').then((m) => ({ default: m.EscalationSettings })),
);
const AnalyticsPage = lazy(() =>
  import('@/features/analytics').then((m) => ({ default: m.AnalyticsPage })),
);
const TemplatesPage = lazy(() =>
  import('@/features/templates').then((m) => ({ default: m.TemplatesPage })),
);
const MediaLibraryPage = lazy(() =>
  import('@/features/media').then((m) => ({ default: m.MediaLibraryPage })),
);
const PostsPage = lazy(() =>
  import('@/features/posts').then((m) => ({ default: m.PostsPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.SettingsPage })),
);
const AdminConsole = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminConsole })),
);
const LandingPage = lazy(() =>
  import('@/features/landing').then((m) => ({ default: m.LandingPage })),
);
const CalendarPage = lazy(() =>
  import('@/features/calendar').then((m) => ({ default: m.CalendarPage })),
);
const SocialFeedsPage = lazy(() =>
  import('@/features/social-feeds').then((m) => ({ default: m.SocialFeedsPage })),
);
const ChannelsPage = lazy(() =>
  import('@/features/channels').then((m) => ({ default: m.ChannelsPage })),
);
const DocsPage = lazy(() =>
  import('@/features/docs').then((m) => ({ default: m.DocsPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.ResetPasswordPage })),
);
const EmailUnsubscribePage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.EmailUnsubscribePage })),
);
// The documents live on the marketing site; these send old app links there.
const PrivacyRedirect = lazy(() =>
  import('@/features/legal').then((m) => ({ default: m.PrivacyRedirect })),
);
const TermsRedirect = lazy(() =>
  import('@/features/legal').then((m) => ({ default: m.TermsRedirect })),
);
const ApprovePage = lazy(() =>
  import('@/features/approve').then((m) => ({ default: m.ApprovePage })),
);
const InvitePage = lazy(() =>
  import('@/features/invitations').then((m) => ({ default: m.InvitePage })),
);
const DataDeletionStatusPage = lazy(() =>
  import('@/features/legal').then((m) => ({ default: m.DataDeletionStatusPage })),
);

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="space-y-4 w-full max-w-md">
        <Skeleton width="100%" height={24} rounded="sm" />
        <Skeleton width="80%" height={16} rounded="sm" />
        <Skeleton width="100%" height={200} rounded="md" />
      </div>
    </div>
  );
}

/** /attention → /overview#attention, keeping the query (the weekly email names its brand). */
function AttentionRedirect(): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={{ pathname: ROUTES.OVERVIEW, search, hash: 'attention' }} replace />;
}

export function App() {
  useVisitTracker();
  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public pages (no auth required) */}
        <Route path={ROUTES.DOCS} element={<DocsPage />} />
        <Route path={ROUTES.PRIVACY} element={<PrivacyRedirect />} />
        <Route path={ROUTES.TERMS} element={<TermsRedirect />} />
        {/* Meta links people here after a Facebook data deletion request. */}
        <Route path={ROUTES.DATA_DELETION} element={<DataDeletionStatusPage />} />
        {/* A client approves posts here without an account (Task 5.1). */}
        <Route path={ROUTES.APPROVE} element={<ApprovePage />} />
        {/* An invitation link (M-01): public, signed in or not; it never mounts a brand screen first. */}
        <Route path={ROUTES.INVITE} element={<InvitePage />} />
        <Route path={ROUTES.INVITES} element={<InvitePage />} />
        {/* NOT guest-only: the emailed recovery link signs the user in (that
            session is what authorises the password change), so GuestGuard
            bounced them to the dashboard before they could set a password. */}
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
        <Route path={ROUTES.EMAIL_UNSUBSCRIBE} element={<EmailUnsubscribePage />} />

        {/* Public — guest-only (redirect to dashboard if already signed in) */}
        <Route element={<GuestGuard />}>
          <Route path={ROUTES.LANDING} element={<LandingPage />} />
          <Route path={ROUTES.AUTH} element={<AuthPage />} />
        </Route>

        {/* Authenticated — requires sign-in */}
        <Route element={<AuthGuard />}>
          {/* The admin console: staff only, in its own frame, with no workspace. */}
          <Route element={<SuperAdminGuard />}>
            <Route path={ROUTES.ADMIN} element={<AdminConsole />} />
          </Route>

          {/* The customer app. A super admin is sent to the console instead. */}
          <Route element={<CustomerOnlyGuard />}>
          <Route path={ROUTES.ONBOARDING} element={<OnboardingPage />} />

          {/* App shell with sidebar/topbar */}
          <Route element={<AppLayout />}>
            {/* Attention is a section of Home now (DS05); old links (emails, notifications) keep working. */}
            <Route path={ROUTES.ATTENTION} element={<AttentionRedirect />} />
            <Route path={ROUTES.PLAN} element={<PlanPage />} />
            {/* The old overview is gone; keep its URL working for bookmarks and emails. */}
            <Route path={ROUTES.OVERVIEW} element={<OverviewPage />} />
            <Route path={ROUTES.DASHBOARD} element={<Navigate to={ROUTES.OVERVIEW} replace />} />
            <Route path={ROUTES.COMPOSER} element={<ComposerPage />} />
            <Route path={ROUTES.COMPOSER_EDIT} element={<ComposerPage />} />
            <Route path={ROUTES.INBOX} element={<InboxPage />} />
            <Route path={ROUTES.INBOX_SETTINGS} element={<InboxSettingsPage />} />
            <Route path={ROUTES.BRAND} element={<BrandPage />} />
            <Route path={ROUTES.BRAND_SETUP} element={<BrandSetupPage />} />
            <Route path={ROUTES.ANALYTICS} element={<AnalyticsPage />} />
            <Route path={ROUTES.POSTS} element={<PostsPage />} />
            {/* Approval queue (every plan) — fully built but previously never
                mounted anywhere. */}
            <Route path={ROUTES.APPROVALS} element={<ApprovalsPage />} />
            <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />
            <Route path={ROUTES.FEEDS} element={<SocialFeedsPage />} />
            <Route path={ROUTES.TEMPLATES} element={<TemplatesPage />} />
            <Route path={ROUTES.CALENDAR} element={<CalendarPage />} />
            <Route path={ROUTES.MEDIA} element={<MediaLibraryPage />} />
            <Route path={ROUTES.CHANNELS} element={<ChannelsPage />} />
            <Route path={ROUTES.TEAM} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
          </Route>
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
